import type { Application, Container, FederatedPointerEvent, FederatedWheelEvent } from "pixi.js";

export interface CameraOptions {
  initialZoom?: number;
  minZoom?: number;
  maxZoom?: number;
  /** ワールドの中心を画面中央に寄せるオフセット */
  centerOffset?: { x: number; y: number };
  /**
   * どのマウスボタンでパン操作を有効化するか。
   * - 既定 (sim 画面): すべて true (左ドラッグでもパン可)
   * - エディタ画面では `{ left: false, middle: true, right: true }` を渡し、
   *   左ボタンをオブジェクト操作に専有させる。
   */
  panButtons?: { left?: boolean; middle?: boolean; right?: boolean };
}

export interface Camera {
  /** 現在のズーム倍率 */
  zoom: number;
  /** パン位置（world 原点のスクリーン上の位置） */
  pan: { x: number; y: number };
  setZoom(next: number): void;
  setPan(x: number, y: number): void;
  centerOn(x: number, y: number): void;
  dispose(): void;
}

/**
 * World コンテナにパン/ズーム制御を付ける。
 * - マウスドラッグでパン
 * - ホイールでズーム（カーソル中心）
 */
export function attachCamera(app: Application, world: Container, opts: CameraOptions = {}): Camera {
  const minZoom = opts.minZoom ?? 1;
  const maxZoom = opts.maxZoom ?? 8;
  const centerOffset = opts.centerOffset ?? { x: 0, y: 0 };

  const state = {
    zoom: opts.initialZoom ?? 3,
    pan: { x: centerOffset.x, y: centerOffset.y },
  };

  function apply() {
    world.position.set(state.pan.x, state.pan.y);
    world.scale.set(state.zoom);
  }

  // drag-to-pan
  let dragging = false;
  let last: { x: number; y: number } | null = null;

  const canvas = app.canvas;
  canvas.style.cursor = "grab";

  app.stage.eventMode = "static";
  app.stage.hitArea = app.screen;

  const panLeft = opts.panButtons?.left ?? true;
  const panMiddle = opts.panButtons?.middle ?? true;
  const panRight = opts.panButtons?.right ?? true;

  function onDown(e: FederatedPointerEvent) {
    if (e.button === 0 && !panLeft) return;
    if (e.button === 1 && !panMiddle) return;
    if (e.button === 2 && !panRight) return;
    dragging = true;
    last = { x: e.global.x, y: e.global.y };
    canvas.style.cursor = "grabbing";
  }

  function onMove(e: FederatedPointerEvent) {
    if (!dragging || !last) return;
    const dx = e.global.x - last.x;
    const dy = e.global.y - last.y;
    last = { x: e.global.x, y: e.global.y };
    state.pan.x += dx;
    state.pan.y += dy;
    apply();
  }

  function onUp() {
    dragging = false;
    last = null;
    canvas.style.cursor = "grab";
  }

  // ブラウザのデフォルト右クリックメニューを抑制 (Pixi 側で右ボタン操作を使うため)
  const onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
  };
  canvas.addEventListener("contextmenu", onContextMenu);

  function onWheel(e: WheelEvent) {
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    // ホイール方向でズーム倍率を変える（カーソル位置を中心に）
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const nextZoom = Math.min(maxZoom, Math.max(minZoom, state.zoom * factor));
    const ratio = nextZoom / state.zoom;

    // カーソル位置がズーム前後で同じワールド座標を指すように pan を補正
    state.pan.x = cx - (cx - state.pan.x) * ratio;
    state.pan.y = cy - (cy - state.pan.y) * ratio;
    state.zoom = nextZoom;
    apply();
  }

  app.stage.on("pointerdown", onDown);
  app.stage.on("pointermove", onMove);
  app.stage.on("pointerup", onUp);
  app.stage.on("pointerupoutside", onUp);
  canvas.addEventListener("wheel", onWheel, { passive: false });

  // FederatedWheelEvent 対応もしておく（将来のPixi変更に備えて）
  const onFedWheel = (e: FederatedWheelEvent) => {
    onWheel(e.nativeEvent as WheelEvent);
  };
  app.stage.on("wheel", onFedWheel);

  apply();

  const camera: Camera = {
    get zoom() {
      return state.zoom;
    },
    set zoom(v: number) {
      state.zoom = Math.min(maxZoom, Math.max(minZoom, v));
      apply();
    },
    get pan() {
      return { ...state.pan };
    },
    set pan(p: { x: number; y: number }) {
      state.pan = { ...p };
      apply();
    },
    setZoom(next: number) {
      state.zoom = Math.min(maxZoom, Math.max(minZoom, next));
      apply();
    },
    setPan(x: number, y: number) {
      state.pan.x = x;
      state.pan.y = y;
      apply();
    },
    centerOn(x: number, y: number) {
      state.pan.x = app.screen.width / 2 - x * state.zoom;
      state.pan.y = app.screen.height / 2 - y * state.zoom;
      apply();
    },
    dispose() {
      app.stage.off("pointerdown", onDown);
      app.stage.off("pointermove", onMove);
      app.stage.off("pointerup", onUp);
      app.stage.off("pointerupoutside", onUp);
      app.stage.off("wheel", onFedWheel);
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("contextmenu", onContextMenu);
    },
  };

  return camera;
}
